import {
  Component,
  ElementRef,
  OnDestroy,
  afterNextRender,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastrService } from 'ngx-toastr';

import { AliasSessionService } from '../../services/alias-session.service';
import { AliasService } from '../../services/alias.service';
import { AuthenticationService } from '../../services/authentication.service';

/**
 * Persistent staff-side banner shown for the duration of a read-only alias
 * (impersonation) session — see `AliasSessionService` (Task 13). Mounted once
 * in `AppComponent`, above the routed shell, so it stays visible across every
 * route in the vendor (PA) portal; it renders nothing when `isAliased()` is
 * false, so it self-hides for every non-aliased session with no host markup
 * left behind.
 *
 * There is deliberately no vendor-facing equivalent — the vendor whose
 * portal is being viewed sees their normal UI, unaware of the read-only
 * staff session riding along. This is a staff guidance affordance, not a
 * vendor-visible feature.
 *
 * "End session" calls `AliasService.endSession()` to clear the server-side
 * alias cookies, then runs a local teardown (see `refreshStaffSessionAndReload`)
 * BEFORE hard-reloading back to the internal landing route: `getStoredUser()`
 * (read synchronously by `internalCanMatch`, a `canMatch` guard, before
 * `AuthGuard`'s own `/me` call runs) is still holding the aliased VENDOR
 * profile at this point, so reloading straight away would bounce the staffer
 * to `/invitations`. Re-confirming `/user/me` here — after the alias cookies
 * are gone — re-stores the STAFF profile first, so the guard sees the right
 * role once the reload lands. Mirrors
 * `AliasVendorPickerDialogComponent.reloadIntoVendorPortal` (Task 14) — the
 * reload is extracted into its own protected method so a spec can `spyOn` it
 * instead of triggering a real navigation (Chrome Headless makes
 * `window.location` non-configurable; see that component's class doc).
 */
@Component({
  standalone: true,
  selector: 'app-alias-banner',
  imports: [CommonModule],
  templateUrl: './alias-banner.component.html',
  styleUrl: './alias-banner.component.scss',
})
export class AliasBannerComponent implements OnDestroy {
  private aliasSessionService = inject(AliasSessionService);
  private aliasService = inject(AliasService);
  private authService = inject(AuthenticationService);
  private toastr = inject(ToastrService);
  private readonly host = inject(ElementRef<HTMLElement>);

  readonly isAliased = this.aliasSessionService.isAliased;
  readonly vendorName = this.aliasSessionService.vendorName;

  /** Guards against a double-click firing a second `endSession` request. */
  ending = false;

  private resizeObserver?: ResizeObserver;

  constructor() {
    // Publish the banner's rendered height as a global CSS var so the vendor
    // shell's sticky rail and topbar can offset their `top` by it (and the rail
    // its `height`), pinning just BELOW this banner instead of underneath it —
    // the banner sits at z-index 1000, above both. Observe the HOST rather than
    // gating on `isAliased()`: when not aliased the inner `.alias-banner` is
    // `*ngIf`'d out, so the host is 0-height and the var resolves to `0px`,
    // leaving non-aliased vendor sessions untouched. ResizeObserver fires on
    // observe and on every height change, so it also catches the 0 -> full
    // transition when the banner content renders after `/user/me` hydrates, and
    // any wrap to a taller banner (long vendor names on narrow desktop widths).
    afterNextRender(() => {
      const el = this.host.nativeElement;
      const publish = () =>
        document.documentElement.style.setProperty(
          '--alias-banner-h',
          `${el.offsetHeight}px`,
        );
      publish();
      this.resizeObserver = new ResizeObserver(publish);
      this.resizeObserver.observe(el);
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    document.documentElement.style.removeProperty('--alias-banner-h');
  }

  endSession(): void {
    if (this.ending) {
      return;
    }
    this.ending = true;
    this.aliasService.endSession().subscribe({
      next: () => this.refreshStaffSessionAndReload(),
      error: () => {
        // The server-side session also auto-expires on its own, so a failed
        // end here shouldn't strand the staffer in the vendor's portal — run
        // the same teardown anyway. If the alias cookie somehow survived, the
        // /user/me call below simply reflects that and the guidance session
        // continues.
        this.toastr.error('Failed to end the alias session cleanly; reloading anyway.');
        this.refreshStaffSessionAndReload();
      },
    });
  }

  /**
   * Shared teardown run after the server-side alias session is gone (whether
   * `endSession()` above succeeded or failed) and before the hard reload.
   *
   * `AuthenticationService.me()` stores whichever user the server currently
   * recognizes — during the alias session that was the aliased VENDOR (see
   * class doc) — so reloading immediately after `endSession()` would still
   * find that stale vendor profile in storage. `endSession()` has already
   * cleared the server-side alias cookies, so this `me()` is sent with only
   * the STAFF cookies and re-stores the STAFF profile on success. A 401 from
   * it (an expired staff token on a long guidance session) carries no
   * `X-Alias-Session-Ended` header, so `authInterceptor` treats it as an
   * ordinary staff refresh — the `clear()` here is local-state cleanup (drop
   * the stale aliased signal so no alias affordances flash before the reload),
   * NOT what routes that 401. On error, reload anyway — same "don't strand the
   * staffer" rationale as `endSession`'s error branch.
   */
  private refreshStaffSessionAndReload(): void {
    this.aliasSessionService.clear();
    this.authService.me().subscribe({
      next: () => this.reloadToInternalDashboard(),
      error: () => this.reloadToInternalDashboard(),
    });
  }

  /**
   * Hard reload back to the internal landing route so the whole app
   * re-bootstraps as the staffer (see class doc). Lands on
   * `/internal/dashboard` — guarded by `AuthGuard`, which calls
   * `GET /user/me` first — same rationale as
   * `AliasVendorPickerDialogComponent.reloadIntoVendorPortal`'s `/dashboard`
   * target, mirrored for the internal side.
   */
  protected reloadToInternalDashboard(): void {
    window.location.href = '/internal/dashboard';
  }
}
